import React, { useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetPaginatedChannels } from 'pages/Board/hooks/useGetPaginatedChannels';
import {
  BoardFilterView,
  BoardPetParentFragment,
  ChannelStatusAction,
  UpdateUserBoardFilterSettingsMutationFn,
} from 'shared/types/graphql';
import BoardCard from '../Cards/BoardCard';
import BoardColumn from './BoardColumn';
import uniqBy from 'lodash-es/uniqBy';
import { useAppSelector } from 'state/hooks';

interface ConversationColumnProps {
  columnName: string;
  backgroundColor: string;
  channelStatusId: string;
  unreadChannelIds?: string[];
  fetchMoreColumns: () => Promise<void>;
  isEmpty?: boolean;
  isCollapsed?: boolean;
  channelStatusAction?: ChannelStatusAction;
  updateBoardSettings: UpdateUserBoardFilterSettingsMutationFn;
}

const ConversationColumn = ({
  columnName,
  backgroundColor,
  channelStatusId,
  unreadChannelIds,
  fetchMoreColumns,
  isEmpty = true,
  isCollapsed = false,
  updateBoardSettings,
  channelStatusAction,
}: ConversationColumnProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { boardSettings } = useAppSelector((state) => state.board);

  const { channels, isLoadingChannels, channelsPage, hasNextPage, setChannelsPage, totalChannels } =
    useGetPaginatedChannels({
      clinicId: currentClinicId || '',
      dateRange: boardSettings?.specificDateRange,
      channelStatusIds: [channelStatusId],
      shouldSkip: isEmpty,
      shouldFetchCountOnly: isCollapsed,
    });

  // Ensure we are only showing unique channels for this channel status id
  const filteredChannels = useMemo(() => {
    const uniqueChannels = uniqBy(channels, 'id');
    return uniqueChannels?.filter((channel) => channel?.channelStatus?.id === channelStatusId);
  }, [channelStatusId, channels]);

  return (
    <BoardColumn
      columnName={columnName}
      columnColor={backgroundColor}
      channelStatusId={channelStatusId}
      numberOfCards={totalChannels || 0}
      isLoading={isLoadingChannels}
      currentPage={channelsPage}
      setPageNumber={setChannelsPage}
      hasNextPage={hasNextPage}
      fetchMoreColumns={fetchMoreColumns}
      isEmpty={isEmpty}
      isCollapsed={isCollapsed}
      updateBoardSettings={updateBoardSettings}
      channelStatusAction={channelStatusAction}
    >
      {filteredChannels?.map((channel, index) => {
        const channelId = channel?.id || '';
        const channelMembers = channel.channelMembers.filter(
          ({ clinicPetParent, removedAt }) => !!clinicPetParent && !removedAt,
        );
        const petParentsData: BoardPetParentFragment[] = channelMembers.map(
          ({ clinicPetParent }) => clinicPetParent as BoardPetParentFragment,
        );
        return (
          <BoardCard
            key={`${channelId}-${index}`}
            cardType={BoardFilterView.Conversations}
            channelStatus={channel.channelStatus}
            channelId={channel.id}
            channelData={channel}
            petParents={petParentsData}
            pets={channel.pets}
            lastMessageCreatedAtDate={channel.lastMessage?.createdAt}
            channelUpdatedAtDate={channel.updatedAt}
            hasUnreadMessages={channelId && unreadChannelIds ? unreadChannelIds.includes(channelId) : false}
          />
        );
      })}
    </BoardColumn>
  );
};

export default ConversationColumn;
